import { useState, type FormEvent } from 'react';
import type { AdminClient, AdminSession } from '@/persistence/adminClient';

/**
 * The code box. Mirrors AccessCodeEntry's markup and classes so it needs no new
 * CSS, with two deliberate differences.
 *
 *  - **No shape validation.** AccessCodeEntry runs `isValidAccessCode`, but the
 *    admin code is a shared secret of unknown shape: validating it here would
 *    both bake its shape into a public bundle and risk rejecting a valid code.
 *    Only "non-empty" is checked, and the verdict is the service's.
 *  - **Three outcomes, not two.** "That code was rejected" and "can't reach the
 *    admin service" send someone to look in completely different places — the
 *    same distinction the prospect canvas already refuses to collapse.
 */
type GateState = 'idle' | 'checking' | 'rejected' | 'throttled' | 'unavailable';

const MESSAGES: Record<Exclude<GateState, 'idle' | 'checking'>, string> = {
  rejected: 'That code was not accepted.',
  throttled: 'Too many attempts. Wait a few minutes and try again.',
  unavailable: 'Can’t reach the admin service. The code may be fine.',
};

interface Props {
  client: AdminClient;
  onUnlocked: (session: AdminSession) => void;
}

export function AdminGate({ client, onUnlocked }: Props) {
  const [code, setCode] = useState('');
  const [state, setState] = useState<GateState>('idle');

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!code.trim() || state === 'checking') return;

    setState('checking');
    const result = await client.unlock(code);
    if (result.status === 'unlocked') {
      /* The typed code goes out of scope with this component. It is never
         stored, and it is never the token — the service mints that. */
      onUnlocked(result.session);
      return;
    }
    setState(result.status);
  };

  return (
    <main className="gate">
      <form className="gate-card" onSubmit={submit}>
        <span className="vitalic-mark">VITALIC</span>
        <h1>Admin</h1>
        <p>Enter an admin code to manage scenarios.</p>

        <label className="gate-field" htmlFor="admin-code">
          <span className="sr-only">Admin code</span>
          <input
            id="admin-code"
            type="password"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            value={code}
            onChange={(event) => {
              setCode(event.target.value);
              setState('idle');
            }}
          />
        </label>

        {state !== 'idle' && state !== 'checking' && (
          <p className="gate-error" role="alert">
            {MESSAGES[state]}
          </p>
        )}

        <button type="submit" disabled={state === 'checking'}>
          {state === 'checking' ? 'Checking…' : 'Unlock'}
        </button>
      </form>
    </main>
  );
}
